import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import { injectIntl, intlShape, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { License } from './schema';

import messages from './messages/LicenseForm';
import propMessages from './messages/LicenseProps';

import Button from '../../components/Button';
import { Input, InputGroup, Label, Select } from '../../components/Form';

const RangeSelector = ({
  includeUpper, key, onChange, rangeMax, rangeMin, selected, stepSize
}) => {
  const steps = (rangeMax - rangeMin) / stepSize + includeUpper;
  const options = Array.from({ length: steps }, (o, step) => {
    const v = rangeMin + step * stepSize;
    return <option value={v} key={`${key}-${step}`}>{v}</option>;
  });
  return (
    <Select
      onChange={onChange}
      value={selected}
    >
      {options}
    </Select>
  );
}

RangeSelector.propTypes = {
  includeUpper: PropTypes.bool,
  key: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  rangeMax: PropTypes.number.isRequired,
  rangeMin: PropTypes.number,
  selected: PropTypes.number,
  stepSize: PropTypes.number,
};

RangeSelector.defaultProps = {
  includeUpper: true,
  rangeMin: 1,
  selected: null,
  stepSize: 1,
}

const PriceSelector = (Selector => {
  /* Should be NOTE'ed that if maxPrice, minPrice and stepSize do not
   * intertvine nicely then good behaviour will not be guaranteed.
   * For now the consensus is that the licenses' price ranges conform to step
   * size of 10 euros.
   */
  const wrapped = ({ maxPrice, minPrice, onChange, selected }) =>
    <Selector
      key='license-price'
      onChange={onChange}
      rangeMax={maxPrice}
      rangeMin={minPrice}
      selected={selected}
      stepSize={10}
    />;
  wrapped.propTypes = {
    maxPrice: PropTypes.number.isRequired,
    minPrice: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    selected: PropTypes.number,
  };
  wrapped.defaultProps = {
    selected: null,
  };
  return wrapped;
})(RangeSelector);

const ExtraMonthsSelector = (Selector => {
  const wrapped = ({ maxMonths, onChange, selected }) =>
    <Selector
      key='license-extraMonths'
      onChange={onChange}
      rangeMax={maxMonths}
      rangeMin={0}
      selected={selected}
      stepSize={1}
    />;
  wrapped.propTypes = {
    maxMonths: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    selected: PropTypes.number,
  };
  wrapped.defaultProps = {
    selected: null,
  };
  return wrapped;
})(RangeSelector);

const datepickerProps = {
  onClick: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
};

const DatePickerInput = ({ onClick, value }) => (
  <Input value={value} onClick={onClick} />
);

DatePickerInput.propTypes = datepickerProps;

/* A note about license billing.
 *
 * License types have certain billing period (usually 12 months) and may
 * provide certain amount of extra months (usually 1) on the first (activation)
 * period.
 *
 * E.g. license type with 12 months billing period and 1 extra
 * months activated today will be active immediately for the next 13 months. And
 * if the license is continuous (renewed automatically) the next bill date will
 * be 13 months from today and then continue in 12 month cycles until the
 * license gets terminated.
 *
 * The extra months are applied only on activation of new license. I.e. not
 * when upgrading from one license type to another. Except when the previous
 * license is still on its extra months, then the remaining extra time is
 * transferred to the new licenses activation period.
 */

const propTypes = {
  currentLicense: PropTypes.object,
  children: PropTypes.func,
  externalControls: PropTypes.bool,
  initialData: PropTypes.object,
  initialSellerId: PropTypes.number,
  intl: intlShape.isRequired,
  isAdd: PropTypes.bool,
  isUpgrade: PropTypes.bool,
  licenseTypes: PropTypes.array,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  sellers: PropTypes.array.isRequired,
};

const defaultProps = {
  currentLicense: null,
  children: () => {},
  externalControls: false,
  initialData: null,
  isAdd: true,
  isUpgrade: false,
  licenseTypes: null,
};

class LicenseForm extends Component {
  constructor(props) {
    super(props);
    this.state = this.getStateFromProps();
  }

  getStateFromProps = () => {
    const {
      currentLicense,
      initialSellerId,
      initialData,
      sellers,
      isUpgrade } = this.props;
    // defaults
    const state = {
      billingDate: moment(),
      continuous: true,
      endDate: null,
      extraMonths: 1,
      licenseType: null,
      priceNoVat: '',
      customerReference: '',
      seller: null,
      startDate: moment(),
    };
    if (initialData) {
      // if initial data is provided use it
      state.billingDate = moment(initialData.oldestLog.data.startDate);
      state.continuous = initialData.data.continuous;
      state.endDate = moment(initialData.data.endDate);
      state.extraMonths = initialData.oldestLog.data.extraMonths;
      state.licenseType = initialData.data.licenseType;
      state.priceNoVat = initialData.oldestLog.data.priceNoVat;
      state.customerReference = initialData.data.customerReference;
      state.seller = initialData.data.seller;
      state.startDate = moment(initialData.data.startDate);
      if (state.licenseType.billingBackend === 'ZECKIT') {
        state.priceNoVat = state.licenseType.rrpNoVat;
      }
    } else if (isUpgrade) {
      // transfer the remaining extra to the new license.
      state.extraMonths = this.remainingExtraMonths;
      // if not inital data and this is upgrade prepopulate customerReference
      // from currently active license
      state.customerReference = currentLicense.data.customerReference;
      // when upgrading the start date is always the date when the upgrade
      // happens.
      state.startDate = moment();
      state.billingDate = moment();
    }
    // if initial value for seller is provided explicitly use it
    state.seller = sellers.find(s => s.id === initialSellerId) || state.seller;
    return state;
  }

  getValueSetter = key => evnt => {
    let value = evnt.target.value;
    if (evnt.target.type === 'checkbox') {
      value = evnt.target.checked;
    }
    this.setState({ [key]: value });
  }

  get durationInCurrentPeriod() {
    /* Get moment.duration of time that has passed in the current period.
     */
    const { currentLicense } = this.props;
    if (!currentLicense || currentLicense.data.status !== 'ordered') {
      return 0;
    }
    const { currentLog } = currentLicense;
    // Calculate the start date for the current period by subtracting the
    // invoicing period from the end date. This way we can ignore the possible
    // extra months.
    const startDate = moment(currentLicense.data.endDate)
      .subtract(currentLog.data.invoicingPeriod, 'months')
      .add(1, 'days');
    const today = moment();
    const diffToToday = today.diff(startDate);
    const duration = moment.duration(diffToToday);
    return duration >= 0 ? duration : moment.duration();
  }

  get remainingExtraMonths() {
    /* Get the amount of remaining extra months (ceiled) for the active license.
     */
    const { currentLicense } = this.props;
    if (!currentLicense || currentLicense.data.status !== 'ordered') {
      return 0;
    }
    const today = moment();
    const passedDuration = today.diff(currentLicense.startDate);
    const extraDuration = moment.duration(currentLicense.extraMonths, 'months');
    const remaining = extraDuration.subtract(passedDuration);
    return Math.max(0, Math.ceil(remaining.asMonths()));
  }

  get compensation() {
    /* Count compensated price for new license when upgrading license type.
     */
    const { isUpgrade } = this.props;
    if (isUpgrade) {
      const {
        currentLicense: {
          currentLog,
          licenseType: { periodInMonths },
        }
      } = this.props;
      if (!currentLog) {
        return 0;
      }
      // Ceil the used months
      const passedMonths = Math.ceil(this.durationInCurrentPeriod.asMonths());
      // Passed months may be larger than the license type's default period if
      // there is extra months applied to the current period.
      const usedPortion = Math.min(passedMonths / periodInMonths, 1);
      const compensation = currentLog.data.priceNoVat * (1 - usedPortion);
      // Round to one cent precision. Why Math.EPSILON? Because of floating
      // point arithmetic where 1.005*100 = 100.49999999999999
      // https://stackoverflow.com/a/11832950
      return Math.round((compensation + Number.EPSILON) * 100) / 100;
    }
    return 0;
  }

  get valid() {
    return !!this.state.licenseType && !!this.state.seller;
  }

  calculateEndDate = (startDate, periodInMonths, extraMonths) => {
    const endDate = startDate.clone()
      .add(extraMonths, 'months')
      .add(periodInMonths, 'months')
      .subtract(1, 'days');
    return endDate;
  }

  selectLicenseType = evnt => {
    const licenseTypeId = evnt.target.value;
    if (licenseTypeId) {
      const { licenseTypes } = this.props;
      const { startDate, extraMonths: initialExtraMonths } = this.state;
      const licenseType = licenseTypes.find(l => l.id === licenseTypeId);
      let extraMonths = initialExtraMonths;
      if (licenseType.extraMonths < initialExtraMonths) {
        extraMonths = licenseType.extraMonths;
      }
      const endDate = this.calculateEndDate(
        startDate,
        licenseType.periodInMonths,
        extraMonths
      );
      const priceNoVat = Math.max(licenseType.rrpNoVat - this.compensation, 0);
      this.setState({
        endDate,
        extraMonths,
        licenseType,
        priceNoVat,
      });
    } else {
      this.setState(this.getStateFromProps());
    }
  }

  selectSeller = evnt => {
    const { sellers } = this.props;
    const userId = evnt.target.value;
    const seller = sellers.find(s => s.id === userId) || null;
    this.setState({ seller });
  }

  selectStartDate = startDate => {
    const { extraMonths, licenseType } = this.state;
    const endDate = this.calculateEndDate(
      startDate,
      licenseType.periodInMonths,
      extraMonths
    );
    this.setState({ endDate, billingDate: startDate, startDate });
  }

  selectExtraMonths = evnt => {
    const extraMonths = parseInt(evnt.target.value, 10);
    const { startDate, licenseType } = this.state;
    const endDate = this.calculateEndDate(
      startDate,
      licenseType.periodInMonths,
      extraMonths
    );
    this.setState({ endDate, extraMonths });
  }

  get submitLabel() {
    const { isAdd } = this.props;
    if (isAdd) {
      return <FormattedMessage {...messages.activationAction} />;
    }
    return <FormattedMessage {...messages.upgradeAction} />;
  }

  submit = evnt => {
    evnt.preventDefault();
    const {
      billingDate,
      continuous,
      customerReference,
      endDate,
      extraMonths,
      licenseType,
      priceNoVat,
      seller,
      startDate,
    } = this.state;
    const data = {
      continuous,
      customerReference,
      extraMonths,
      endDate,
      licenseType,
      log: [{
        customerReference,
        endDate,
        extraMonths,
        priceNoVat,
        startDate: billingDate,
      }],
      priceNoVat,
      retailer: seller.company,
      seller,
      startDate,
    };
    const license = new License(data);
    this.props.onSubmit({ license });
  }

  cancel = evnt => {
    evnt.preventDefault();
    this.setState(this.getStateFromProps(), this.props.onCancel)
  }

  render = () => {
    const {
      currentLicense,
      children,
      externalControls,
      isAdd,
      isUpgrade,
      licenseTypes,
      sellers } = this.props;
    const {
      continuous,
      customerReference,
      endDate,
      extraMonths,
      licenseType,
      priceNoVat,
      seller,
      startDate,
    } = this.state;

    return (
      <React.Fragment>
        <InputGroup>
          <Select
            id="license-form-licenseType-input"
            onChange={this.selectLicenseType}
            value={licenseType ? licenseType.id : ''}
          >
            <option>
              {this.props.intl.formatMessage(messages.emptyLicenseTypeLabel)}
            </option>
            {licenseTypes.filter(l =>
              isAdd || l.id !== currentLicense.licenseType.id
            ).map(l =>
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            )}
          </Select>
        </InputGroup>
        {licenseType && (
          <React.Fragment>
            <InputGroup>
              <Label htmlFor="license-form-seller-input">
                <FormattedMessage {...propMessages['seller.username']} />
              </Label>
              <Select
                id="license-form-seller-input"
                onChange={this.selectSeller}
                value={seller ? seller.id : ''}
              >
                <option>
                  {this.props.intl.formatMessage(messages.emptySellerLabel)}
                </option>
                {sellers.map(s =>
                  <option key={s.id} value={s.id}>
                    {s.email}
                  </option>
                )}
              </Select>
            </InputGroup>
            <InputGroup>
              <Label htmlFor="license-form-startDate-input">
                <FormattedMessage {...propMessages['license.startDate']} />
              </Label>
              {isUpgrade ? (
                <Value id="license-form-startDate-input">
                  {startDate.format('L')}
                </Value>
              ) : (
                <React.Fragment>
                  <HelpText>
                    <FormattedMessage
                      {...messages.licenseLengthHelpText}
                      values={{
                        periodInMonths: licenseType.periodInMonths,
                      }}
                    />
                  </HelpText>
                  <DatePicker
                    customInput={<DatePickerInput />}
                    dateFormat='L'
                    id="license-form-startDate-input"
                    onChange={this.selectStartDate}
                    selected={startDate}
                  />
                </React.Fragment>
              )}
            </InputGroup>
            {isAdd && (
              <InputGroup>
                <Label id="license-form-extraMonths-input">
                  <FormattedMessage {...propMessages['license.extraMonths']} />
                </Label>
                <HelpText>
                  <FormattedMessage {...messages.licenseExtraMonthsHelpText} />
                </HelpText>
                <ExtraMonthsSelector
                  id="license-form-extraMonths-input"
                  maxMonths={licenseType.extraMonths}
                  selected={extraMonths}
                  onChange={this.selectExtraMonths}
                />
              </InputGroup>
            )}
            <InputGroup>
              <Label htmlFor="license-form-continuous-input">
                <Input
                  id="license-form-continuous-input"
                  type='checkbox'
                  checked={continuous}
                  onChange={this.getValueSetter('continuous')}
                />
                <FormattedMessage {...propMessages['license.continuous']} />
              </Label>
              <HelpText>
                <FormattedMessage {...messages.licenseContinuousHelpText} />
              </HelpText>
            </InputGroup>
            <InputGroup>
              <Label htmlFor="license-form-endDate-input">
                <FormattedMessage {...propMessages['license.endDate']} />
              </Label>
              <HelpText>
                <FormattedMessage
                  {...messages.licenseEndDateExtraMonthsHelpText}
                  values={{ extraMonths }}
                />
              </HelpText>
              {continuous && (
                <HelpText>
                  <FormattedMessage
                    {...messages.licenseEndDateContinuousHelpText}
                  />
                </HelpText>
              )}
              <Value id="license-form-endDate-input">
                {endDate.format('L')}
              </Value>
            </InputGroup>
            {licenseType.billingBackend === 'lima' && (
              <InputGroup>
                <Label htmlFor="license-form-price-input">
                  <FormattedMessage
                    {...propMessages['licenseLog.priceNoVat']}
                  />
                </Label>
                {(isAdd && licenseType.maxPriceNoVat > 0) && (
                  <React.Fragment>
                    <HelpText>
                      <FormattedMessage
                        {...messages.licensePriceActivationHelpText}
                        values={{
                          minPrice: licenseType.minPriceNoVat,
                          maxPrice: licenseType.maxPriceNoVat,
                        }}
                      />
                    </HelpText>
                    <PriceSelector
                      id="license-form-price-input"
                      maxPrice={licenseType.maxPriceNoVat}
                      minPrice={licenseType.minPriceNoVat}
                      selected={parseInt(priceNoVat, 10)}
                      onChange={this.getValueSetter('priceNoVat')}
                    />
                  </React.Fragment>
                )}
                {isUpgrade && (
                  <HelpText>
                    <FormattedMessage
                      {...messages.licensePriceUpgradeHelpText}
                      values={{
                        rrp: licenseType.rrpNoVat,
                        compensation: this.compensation,
                      }}
                    />
                  </HelpText>
                )}
                {(isUpgrade || licenseType.maxPriceNoVat === 0) && (
                  <Value id="license-form-price-input">
                    {priceNoVat}&euro;
                  </Value>
                )}
              </InputGroup>
            )}
            <InputGroup>
              <Label htmlFor="license-form-customerReference-input">
                <FormattedMessage
                  {...propMessages['license.customerReference']}
                />
              </Label>
              <Input
                id="license-form-customerReference-input"
                type='text'
                value={customerReference}
                onChange={this.getValueSetter('customerReference')}
              />
            </InputGroup>
          </React.Fragment>
        )}
        {externalControls && children({
          onSubmit: this.submit,
          onCancel: this.cancel,
          valid: this.valid,
        })}
        {!externalControls && (
          <React.Fragment>
            <Button
              color='green'
              disabled={!this.valid}
              id="license-form-continue-button"
              onClick={this.submit}
            >
              {this.submitLabel}
            </Button>
            <Button
              color='grey'
              id="license-form-back-button"
              onClick={this.cancel}
            >
              <FormattedMessage {...messages.cancellationAction} />
            </Button>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }
}

LicenseForm.propTypes = propTypes;
LicenseForm.defaultProps = defaultProps;

const HelpText = styled.div`
  display: block;
  font-size: 10pt;
`;

const Value = styled.div`
  display: block;
`;

export default injectIntl(LicenseForm);
